import { useParams, Navigate, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import CTASection from "@/components/sections/CTASection";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal } from "@/components/motion/Reveal";
import { getArticle, publishedArticles } from "@/content/insights";
import { brand as brandInfo } from "@/content/site";
import { ArrowLeft } from "lucide-react";

export default function Article() {
  const { slug } = useParams();
  const article = getArticle(slug);

  // Not found, or a draft (unpublished) — do not index / serve as live content.
  if (!article || !article.published) return <Navigate to="/404" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    datePublished: article.date,
    articleSection: article.category,
    author: { "@type": "Organization", name: brandInfo.name },
    publisher: {
      "@type": "Organization",
      name: brandInfo.name,
      logo: { "@type": "ImageObject", url: brandInfo.logo },
    },
    mainEntityOfPage: `${brandInfo.siteUrl}/insights/${article.slug}`,
  };

  const related = publishedArticles
    .filter((a) => a.slug !== article.slug)
    .slice(0, 2);

  return (
    <>
      <SEO
        title={`${article.title} | Green Circle Group Insights`}
        description={article.excerpt}
        path={`/insights/${article.slug}`}
        jsonLd={jsonLd}
      />

      <article className="bg-white pt-28 md:pt-36 pb-16">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Insights", to: "/insights" },
              { label: article.title, to: `/insights/${article.slug}` },
            ]}
          />
          <div className="mt-8">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-mint">
              {article.category}
            </span>
            <h1 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.08] text-forest text-balance">
              {article.title}
            </h1>
            <p className="mt-4 text-sm text-mid-neutral">
              {new Date(article.date).toLocaleDateString("en-AU", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}{" "}
              · {article.readMinutes} min read
            </p>
          </div>

          <div className="mt-10 space-y-6">
            {article.body.map((p, i) => (
              <Reveal key={i} delay={i * 0.03}>
                <p className="text-lg text-dark-neutral leading-relaxed">{p}</p>
              </Reveal>
            ))}
          </div>

          <Link
            to="/insights"
            className="mt-12 inline-flex items-center gap-2 text-forest font-semibold hover:text-mint transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Insights
          </Link>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-soft py-16 md:py-20">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            <h2 className="text-2xl font-extrabold text-forest">Keep reading</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/insights/${a.slug}`}
                  className="rounded-2xl bg-white border border-border-neutral p-6 hover:border-mint transition-colors"
                >
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-mint">
                    {a.category}
                  </span>
                  <h3 className="mt-2 text-lg font-extrabold text-forest leading-snug">
                    {a.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </>
  );
}
